import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    BookOpen,
    ChevronRight,
    CircleHelp,
    ListChecks,
    Route,
} from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Text,
} from '@vnetwork/datacentral-ui/primitives'
import TopbarAction from '@/components/TopbarAction'
import { useTour } from '@/components/TourProvider'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelpContext } from '@/contexts/quick-help'
import { getAllTours } from '@/services/tourService'
import { DOCS_URL } from '@/app/docs-url'

/** Let the menu close and give focus back before a tour measures the page. */
const AFTER_MENU_CLOSE_MS = 150

/**
 * The one help entry (topbar). Replaces the floating tour pill, the floating
 * "?" quick-help button and the sidebar "Hướng dẫn sử dụng" link:
 *   - this page's guided tour (a page's own handler, else a release-notes tour)
 *   - this page's quick-help questions ("Bạn muốn làm gì?"), when it has any
 *   - the user documentation
 * It is a menu, not a docked panel, so it can be open next to the what's-new,
 * alerts or notifications panels.
 *
 * `scope="sheet"` is the same menu for a modal sheet (drawer) that covers the
 * topbar: the sheet's header carries its own "?" (same trigger, same quick-help
 * options: the sheet registers them while it is open). The page tour describes
 * the page behind the sheet, so it is left out there.
 */
interface HelpMenuProps {
    /** `page` (default): the topbar entry. `sheet`: the entry inside a modal sheet. */
    scope?: 'page' | 'sheet'
}

const HelpMenu = ({ scope = 'page' }: HelpMenuProps) => {
    const inSheet = scope === 'sheet'
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const { startTour } = useTour()
    const { hasCustomHandler, executeHandler } = useTourHandler()
    const { options, runOption } = useQuickHelpContext()

    const releaseTour = useMemo(
        () =>
            getAllTours().find((tour) => pathname.startsWith(tour.route)) ??
            null,
        [pathname]
    )
    const hasTour = hasCustomHandler || releaseTour !== null

    const [menuOpen, setMenuOpen] = useState(false)
    const [afterClose, setAfterClose] = useState(false)

    const handleStartTour = () => {
        window.setTimeout(() => {
            // A page that registered its own handler (multi-step forms, dashboards)
            if (hasCustomHandler) {
                executeHandler()
                return
            }
            if (releaseTour) {
                startTour(
                    releaseTour.steps.map((step) => ({
                        selector: step.selector,
                        content: step.content,
                        title: step.title,
                        position: step.position,
                    }))
                )
            }
        }, AFTER_MENU_CLOSE_MS)
    }

    const label = t('help-menu.title')

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                // Closing hands focus back to the button: keep its tooltip shut
                // until the pointer or focus leaves.
                setMenuOpen(open)
                if (!open) setAfterClose(true)
            }}
        >
            <DropdownMenuTrigger asChild>
                <TopbarAction
                    icon={CircleHelp}
                    aria-label={label}
                    tooltip={label}
                    data-testid={
                        inSheet
                            ? 'help-menu-trigger-sheet'
                            : 'help-menu-trigger'
                    }
                    suppressTooltip={menuOpen || afterClose}
                    onPointerLeave={() => setAfterClose(false)}
                    onBlur={() => setAfterClose(false)}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-80 max-w-[calc(100vw-1rem)]"
                data-testid={inSheet ? 'help-menu-sheet' : 'help-menu'}
            >
                {inSheet ? null : (
                    <DropdownMenuItem
                        disabled={!hasTour}
                        onSelect={handleStartTour}
                        data-testid="help-start-tour"
                    >
                        <Route size={16} />
                        <span className="min-w-0 flex-1">
                            <Text as="div" size="sm" weight="medium">
                                {t('help-menu.start-tour')}
                            </Text>
                            <Text as="div" size="xs" tone="muted">
                                {hasTour
                                    ? t('help-menu.start-tour-hint')
                                    : t('help-menu.no-tour')}
                            </Text>
                        </span>
                        {hasCustomHandler ? (
                            <kbd className="hidden shrink-0 rounded md:inline-block border border-border-subtle px-1.5 text-xs text-muted-foreground">
                                ?
                            </kbd>
                        ) : null}
                    </DropdownMenuItem>
                )}

                {options.length > 0 ? (
                    <>
                        {inSheet ? null : <DropdownMenuSeparator />}
                        <Text
                            as="div"
                            size="xs"
                            weight="medium"
                            tone="muted"
                            className="px-2 py-1"
                        >
                            {t('help-menu.quick-help')}
                        </Text>
                        <div className="max-h-[40vh] overflow-y-auto">
                            {options.map((option, index) => (
                                <DropdownMenuItem
                                    key={option.id}
                                    data-testid={`help-quick-${index}`}
                                    onSelect={() =>
                                        window.setTimeout(
                                            () => runOption(option),
                                            AFTER_MENU_CLOSE_MS
                                        )
                                    }
                                >
                                    <ListChecks size={16} />
                                    <span className="min-w-0 flex-1">
                                        <Text
                                            as="div"
                                            size="sm"
                                            weight="medium"
                                        >
                                            {option.label}
                                        </Text>
                                        <Text as="div" size="xs" tone="muted">
                                            {option.description}
                                        </Text>
                                    </span>
                                    <ChevronRight
                                        size={14}
                                        className="shrink-0 text-muted-foreground"
                                    />
                                </DropdownMenuItem>
                            ))}
                        </div>
                    </>
                ) : null}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onSelect={() =>
                        window.open(DOCS_URL, '_blank', 'noopener,noreferrer')
                    }
                >
                    <BookOpen size={16} />
                    {t('help-menu.docs')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default HelpMenu
